import { useEffect, useRef } from 'react'
import Link from 'next/link'
import useSWRInfinite from 'swr/infinite'
import { motion } from 'framer-motion'
import { CheckCircle, ExternalLink } from 'lucide-react'
import { useTranslations } from 'next-intl'

import type { Asset } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/hooks/use-editor-store'
import { useProjectStore } from '@/hooks/use-project-store'

type UnsplashImage = {
  id: string
  urls: {
    regular: string
    thumb: string
  }
  alt_description: string
  user: {
    name: string
  }
}

type UnsplashSearchResponse = {
  results: UnsplashImage[]
  total: number
}

type UnsplashResponse = UnsplashImage[] | UnsplashSearchResponse

const PER_PAGE = 12

interface ImageLibraryProps {
  query: string
  selectedAssets: Asset[]
  onSelect: (e: React.MouseEvent, asset: Asset) => void
  limit?: number
  isExpanded?: boolean
}

function isSearchResponse(
  data: UnsplashResponse,
): data is UnsplashSearchResponse {
  return (
    !Array.isArray(data) && 'results' in data && Array.isArray(data.results)
  )
}

export const ImageLibrary = ({
  query,
  selectedAssets,
  onSelect,
  limit,
  isExpanded = false,
}: ImageLibraryProps) => {
  const t = useTranslations('image-library')
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const { setIsToolboxOpen } = useProjectStore()
  const {
    getIsEditorOn,
    getIsTargetImage,
    getIsTargetVideo,
    setTargetSrc,
    setTargetSrcWithType,
  } = useEditorStore()
  const isEditorOn = getIsEditorOn()
  const isImageSelectedInEditor = isEditorOn && getIsTargetImage()
  const isVideoSelectedInEditor = isEditorOn && getIsTargetVideo()
  // Allow media type switching: image can be selected when video is selected
  const isMediaSelectedInEditor =
    isImageSelectedInEditor || isVideoSelectedInEditor

  const getKey = (
    pageIndex: number,
    previousPageData: UnsplashResponse | null,
  ) => {
    if (limit && pageIndex > 0) return null // Only fetch first page if limit is set
    if (previousPageData) {
      const hasMore = isSearchResponse(previousPageData)
        ? previousPageData.results.length === PER_PAGE
        : previousPageData.length === PER_PAGE
      if (!hasMore) return null
    }
    const searchParam = query ? `query=${encodeURIComponent(query)}&` : ''
    return `/api/media/unsplash?${searchParam}orderBy=popular&page=${pageIndex + 1}&per_page=${limit || PER_PAGE}`
  }

  const { data, size, setSize, isLoading } = useSWRInfinite<UnsplashResponse>(
    getKey,
    async (url) => {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Failed to fetch')
      return res.json()
    },
    {
      revalidateFirstPage: false,
    },
  )

  const allImages = data
    ? data.flatMap((page) => (isSearchResponse(page) ? page.results : page))
    : []
  // Remove duplicate images based on id
  const uniqueImages = allImages.filter(
    (image, index, self) => index === self.findIndex((i) => i.id === image.id),
  )
  const images = limit ? uniqueImages.slice(0, limit) : uniqueImages
  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty =
    !data?.[0] ||
    (isSearchResponse(data[0])
      ? data[0].results.length === 0
      : data[0].length === 0)
  const lastPage = data?.[data.length - 1]
  const isReachingEnd =
    isEmpty ||
    (lastPage &&
      (isSearchResponse(lastPage)
        ? lastPage.results.length < PER_PAGE
        : lastPage.length < PER_PAGE))

  useEffect(() => {
    if (!loadMoreRef.current || isLoadingMore || isReachingEnd || limit) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setSize(size + 1)
        }
      },
      { rootMargin: '50px' },
    )

    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [isLoadingMore, isReachingEnd, setSize, size, limit])

  const handleImageSelect = (image: UnsplashImage, e: React.MouseEvent) => {
    const assetData: Asset = {
      source: 'elements',
      type: 'image',
      link: image.urls.regular,
      thumbnail: image.urls.thumb,
    }

    // Always call onSelect first (for modal/parent component usage)
    onSelect(e, assetData)

    // If media is selected in editor (not in modal), also update the target directly
    if (isMediaSelectedInEditor) {
      setIsToolboxOpen(false)
      setTargetSrcWithType(image.urls.regular, 'image')
    }
  }

  if (isLoadingMore && size === 1) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('loading')}</p>
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">{t('no_images')}</p>
      </div>
    )
  }

  return (
    <>
      {images.map((image, index) => (
        <motion.div
          key={`${image.id}-${index}`}
          className={cn(
            'flex-none relative cursor-pointer aspect-video',
            isExpanded ? 'w-full' : 'w-[200px]',
            'cursor-pointer',
          )}
          style={isExpanded ? { paddingTop: '56.25%' } : {}}
          onClick={(e) => handleImageSelect(image, e)}
        >
          <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group">
            <img
              src={image.urls.thumb}
              alt={image.alt_description || t('image_alt')}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
            <p className="text-xs text-white line-clamp-2">
              {t('image_by', { name: image.user.name })}
            </p>
          </div>
          {selectedAssets.some(
            (asset) => asset.link === image.urls.regular,
          ) && (
            <div className="absolute top-2 left-2 bg-brand text-white rounded-full p-1">
              <CheckCircle className="w-4 h-4" />
            </div>
          )}
          <div className="absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Link
              href={image.urls.regular}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-300 hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      ))}
      {!limit && <div ref={loadMoreRef} className="h-4" />}
      {isLoadingMore && !limit && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('loading_more')}
          </p>
        </div>
      )}
    </>
  )
}
